import * as Sentry from "@sentry/react";

type SentryLogLevel = "error" | "warning";

const consoleByLevel: Record<SentryLogLevel, (...args: unknown[]) => void> = {
  error: (...args) => console.error(...args),
  warning: (...args) => console.warn(...args),
};

type AxiosConfig = {
  headers?: Record<string, unknown> & {
    get?: (name: string) => unknown;
  };
};

const traceParentPattern = /^00-([0-9a-f]{32})-[0-9a-f]{16}-[0-9a-f]{2}$/i;

const getTraceIdFromTraceParent = (traceParent: unknown): string | undefined => {
  if (typeof traceParent !== "string") {
    return undefined;
  }

  return traceParent.match(traceParentPattern)?.[1];
};

const getTraceIdFromConfig = (config: unknown): string | undefined => {
  if (!config || typeof config !== "object") {
    return undefined;
  }

  const headers = (config as AxiosConfig).headers;
  if (!headers) {
    return undefined;
  }

  const traceParent = typeof headers.get === "function" ? headers.get("traceparent") : headers.traceparent;

  return getTraceIdFromTraceParent(traceParent);
};

const getTraceIdFromError = (error: unknown): string | undefined => {
  if (!error || typeof error !== "object") {
    return undefined;
  }

  return getTraceIdFromConfig("config" in error ? error.config : error);
};

const getTraceIdFromArgs = (args: readonly unknown[]): string | undefined => {
  for (const arg of args) {
    const traceId = getTraceIdFromError(arg);
    if (traceId) {
      return traceId;
    }
  }

  return undefined;
};

const withTraceIdScope = (traceId: string | undefined, callback: () => void): void => {
  if (!traceId) {
    callback();
    return;
  }

  Sentry.withScope((scope) => {
    scope.setTag("traceId", traceId);
    scope.setAttribute("traceId", traceId);
    callback();
  });
};

export const captureSentryLog = (level: SentryLogLevel, ...args: unknown[]): void => {
  withTraceIdScope(getTraceIdFromArgs(args), () => {
    consoleByLevel[level](...args);
  });
};

const captureSentryEvent = (level: SentryLogLevel, ...args: unknown[]): void => {
  const errorArg = args.find((arg): arg is Error => arg instanceof Error);
  const nonErrorArgs = args.filter((arg) => !(arg instanceof Error));
  const extra = nonErrorArgs.length ? { args: nonErrorArgs } : undefined;
  const traceId = getTraceIdFromArgs(args);

  withTraceIdScope(traceId, () => {
    if (errorArg) {
      Sentry.captureException(errorArg, {
        level,
        extra,
      });
    } else {
      Sentry.captureMessage(String(args[0]), {
        level,
        extra,
      });
    }

    consoleByLevel[level](...args);
  });
};

export const captureSentryError = (...args: unknown[]): void => {
  captureSentryEvent("error", ...args);
};

export const captureSentryWarning = (...args: unknown[]): void => {
  captureSentryEvent("warning", ...args);
};
